
import { useState, useEffect } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { formSchema } from "@/types/social-groups";

export interface SocialGroupDB {
  id: string;
  name_ar: string;
  name_en: string;
  description_ar: string;
  description_en: string;
  type: "whatsapp" | "telegram";
  link: string;
  members: number;
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialGroupType {
  id: string;
  name: { ar: string; en: string };
  description: { ar: string; en: string };
  type: "whatsapp" | "telegram";
  link: string;
  members: number;
  image_url?: string | null;
}

const mapDBToGroup = (dbGroup: SocialGroupDB): SocialGroupType => ({
  id: dbGroup.id,
  name: { ar: dbGroup.name_ar, en: dbGroup.name_en },
  description: { ar: dbGroup.description_ar, en: dbGroup.description_en },
  type: dbGroup.type,
  link: dbGroup.link,
  members: dbGroup.members,
  image_url: dbGroup.image_url,
});

export const useSocialGroups = (language: string) => {
  const isRTL = language === "ar";
  
  // Groups state
  const [groups, setGroups] = useState<SocialGroupType[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<SocialGroupType[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingGroup, setEditingGroup] = useState<SocialGroupType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Fetch groups from database
  const fetchGroups = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('social_groups')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching groups:', error);
      toast.error(isRTL ? "خطأ في جلب المجموعات" : "Error fetching groups");
    } else {
      const mappedGroups = (data as SocialGroupDB[]).map(mapDBToGroup);
      setGroups(mappedGroups);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // Filter groups when search term or groups change
  useEffect(() => {
    let result = groups;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = groups.filter(group => 
        group.name.ar.toLowerCase().includes(term) || 
        group.name.en.toLowerCase().includes(term) ||
        group.description.ar.toLowerCase().includes(term) ||
        group.description.en.toLowerCase().includes(term)
      );
    }
    
    setFilteredGroups(result);
    setCurrentPage(1); // Reset current page
  }, [groups, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const currentItems = filteredGroups.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Dialog handlers
  const openAddDialog = () => {
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (group: SocialGroupType) => {
    setEditingGroup(group);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (group: SocialGroupType) => {
    setEditingGroup(group);
    setIsDeleteDialogOpen(true);
  };

  const openViewDialog = (group: SocialGroupType) => {
    setEditingGroup(group);
    setIsViewDialogOpen(true);
  };

  // CRUD operations
  const handleAddGroup = async (data: z.infer<typeof formSchema>) => {
    const { error } = await supabase
      .from('social_groups')
      .insert({
        name_ar: data.name_ar,
        name_en: data.name_en,
        description_ar: data.description_ar,
        description_en: data.description_en,
        type: data.type,
        link: data.link,
        members: data.members,
        image_url: data.image_url || null,
      });
    
    if (error) {
      console.error('Error adding group:', error);
      toast.error(isRTL ? "خطأ في إضافة المجموعة" : "Error adding group");
    } else {
      await fetchGroups();
      setIsAddDialogOpen(false);
      toast.success(isRTL ? "تمت إضافة المجموعة بنجاح" : "Group added successfully");
    }
  };

  const handleEditGroup = async (data: z.infer<typeof formSchema>) => {
    if (!editingGroup) return;
    
    const { error } = await supabase
      .from('social_groups')
      .update({
        name_ar: data.name_ar,
        name_en: data.name_en,
        description_ar: data.description_ar,
        description_en: data.description_en,
        type: data.type,
        link: data.link,
        members: data.members,
        image_url: data.image_url || null,
      })
      .eq('id', editingGroup.id);
    
    if (error) {
      console.error('Error updating group:', error);
      toast.error(isRTL ? "خطأ في تحديث المجموعة" : "Error updating group");
    } else {
      await fetchGroups();
      setIsEditDialogOpen(false);
      toast.success(isRTL ? "تم تحديث المجموعة بنجاح" : "Group updated successfully");
    }
  };

  const handleDeleteGroup = async () => {
    if (!editingGroup) return;
    
    const { error } = await supabase
      .from('social_groups')
      .delete()
      .eq('id', editingGroup.id);
    
    if (error) {
      console.error('Error deleting group:', error);
      toast.error(isRTL ? "خطأ في حذف المجموعة" : "Error deleting group");
    } else {
      await fetchGroups();
      setIsDeleteDialogOpen(false);
      toast.success(isRTL ? "تم حذف المجموعة بنجاح" : "Group deleted successfully");
    }
  };

  return {
    groups,
    filteredGroups,
    currentItems,
    searchTerm,
    setSearchTerm,
    currentPage,
    totalPages,
    setCurrentPage,
    editingGroup,
    isAddDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen, 
    isViewDialogOpen,
    setIsAddDialogOpen,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsViewDialogOpen,
    openAddDialog,
    openEditDialog,
    openDeleteDialog,
    openViewDialog,
    handleAddGroup,
    handleEditGroup,
    handleDeleteGroup,
    isLoading
  };
};
